import { Link, useParams } from 'react-router-dom';
import { episodes } from '@/mocks/episodes';
import { actors } from '@/mocks/actors';
import EpisodeCard from '@/components/feature/EpisodeCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import usePageMeta from '@/hooks/usePageMeta';

export default function EpisodeDetail() {
  const { id } = useParams<{ id: string }>();
  const episode = episodes.find((item) => String(item.id) === id);

  usePageMeta({
    title: episode
      ? `第${String(episode.number).padStart(2, '0')}集 ${episode.title}剧情介绍 - 女友的妈妈`
      : '剧集详情 - 女友的妈妈',
    description: episode
      ? `《女友的妈妈》第${episode.number}集《${episode.title}》剧情介绍：${episode.synopsis.slice(0, 60)}`
      : '《女友的妈妈》剧集详情页',
    keywords: '女友的妈妈分集剧情,女友的妈妈剧情介绍,电视剧剧集详情',
  });

  if (!episode) {
    return (
      <div className="w-full px-4 md:px-8 py-16">
        <div className="max-w-[800px] mx-auto">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '剧集列表', to: '/episodes' }, { label: '剧集详情' }]} />
          <div className="mt-8">
            <EmptyState
              icon="ri-error-warning-line"
              title="该剧集信息暂未发布"
              description="你访问的剧集可能已下线或链接有误，返回剧集列表看看其他内容吧。"
              actionLabel="返回剧集列表"
              onAction={() => {
                window.REACT_APP_NAVIGATE?.('/episodes');
              }}
            />
            <div className="mt-6 text-center">
              <Link to="/episodes" className="text-sm text-primary-600 hover:text-accent-600 transition-colors">
                返回剧集列表
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sorted = [...episodes].sort((a, b) => a.number - b.number);
  const index = sorted.findIndex((item) => item.id === episode.id);
  const prev = index > 0 ? sorted[index - 1] : null;
  const next = index < sorted.length - 1 ? sorted[index + 1] : null;

  const related = sorted
    .filter((item) => item.id !== episode.id)
    .slice(Math.max(0, index - 1), Math.max(0, index - 1) + 3);

  const cast = actors.filter((actor) => episode.actorIds.includes(actor.id));

  const metaRows = [
    { label: '播出日期', value: episode.airDate, icon: 'ri-calendar-line' },
    { label: '导演', value: episode.director, icon: 'ri-clapperboard-line' },
    { label: '编剧', value: episode.writer, icon: 'ri-quill-pen-line' },
    { label: '时长', value: episode.duration, icon: 'ri-time-line' },
  ];

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="max-w-[1080px] mx-auto px-4 md:px-8 py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '剧集列表', to: '/episodes' },
              { label: `第${String(episode.number).padStart(2, '0')}集` },
            ]}
          />
        </div>
      </section>

      <article className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-[1080px] mx-auto">
          <header className="mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium">
              <i className="ri-play-list-2-line"></i>
              第 {String(episode.number).padStart(2, '0')} 集
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-3">
              {episode.title}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">女友的妈妈 · 全集剧情介绍</p>
          </header>

          <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-background-200">
            <img
              src={episode.cover}
              alt={`女友的妈妈 第${episode.number}集 ${episode.title} 剧集封面大图`}
              title={`女友的妈妈 第${episode.number}集 ${episode.title} 剧情介绍`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-5">
            {metaRows.map((row) => (
              <div key={row.label} className="rounded-lg border border-background-200 bg-background-50 p-3.5">
                <dt className="flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className={`${row.icon} text-primary-500`}></i>
                  {row.label}
                </dt>
                <dd className="text-sm md:text-base font-medium text-foreground-900 mt-1.5 line-clamp-1">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
            <div className="lg:col-span-2 space-y-8">
              <section>
                <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-3">
                  <i className="ri-book-open-line text-primary-600"></i>
                  剧情简介
                </h2>
                <p className="text-sm md:text-base leading-loose text-foreground-700">
                  {episode.synopsis}
                </p>
              </section>

              <section className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h2 className="flex items-center gap-2 text-base font-bold text-accent-900 mb-2">
                  <i className="ri-sparkling-2-line"></i>
                  本集看点
                </h2>
                <p className="text-sm md:text-base leading-relaxed text-accent-900/90">
                  {episode.highlight}
                </p>
              </section>
            </div>

            <aside>
              <section className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950 mb-4">
                  <i className="ri-user-star-line text-primary-600"></i>
                  本集演员
                </h2>
                <div className="space-y-3">
                  {cast.map((actor) => (
                    <Link
                      key={actor.id}
                      to={`/actor/${actor.id}`}
                      className="group flex items-center gap-3 rounded-md p-2 hover:bg-background-100 transition-colors"
                    >
                      <span className="w-11 h-11 rounded-full overflow-hidden shrink-0 bg-background-200">
                        <img
                          src={actor.avatar}
                          alt={`演员 ${actor.name}`}
                          title={`${actor.name} 女友的妈妈 演员资料`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                          {actor.name}
                        </span>
                        <span className="block text-xs text-foreground-500 truncate">{actor.role}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            </aside>
          </div>

          <nav className="flex items-stretch gap-3 md:gap-4 mt-10">
            {prev ? (
              <Link
                to={`/episode/${prev.id}`}
                className="group flex-1 rounded-lg border border-background-200 bg-background-50 p-4 hover:border-primary-300 transition-colors"
              >
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-arrow-left-s-line"></i>
                  上一集
                </span>
                <span className="block text-sm md:text-base font-medium text-foreground-900 mt-1 group-hover:text-primary-600 transition-colors line-clamp-1">
                  第{String(prev.number).padStart(2, '0')}集 · {prev.title}
                </span>
              </Link>
            ) : (
              <span className="flex-1 rounded-lg border border-dashed border-background-300 p-4 text-xs text-foreground-400 flex items-center justify-center">
                已是第一集
              </span>
            )}
            {next ? (
              <Link
                to={`/episode/${next.id}`}
                className="group flex-1 rounded-lg border border-background-200 bg-background-50 p-4 text-right hover:border-primary-300 transition-colors"
              >
                <span className="flex items-center justify-end gap-1 text-xs text-foreground-500">
                  下一集
                  <i className="ri-arrow-right-s-line"></i>
                </span>
                <span className="block text-sm md:text-base font-medium text-foreground-900 mt-1 group-hover:text-primary-600 transition-colors line-clamp-1">
                  第{String(next.number).padStart(2, '0')}集 · {next.title}
                </span>
              </Link>
            ) : (
              <span className="flex-1 rounded-lg border border-dashed border-background-300 p-4 text-xs text-foreground-400 flex items-center justify-center">
                已是大结局
              </span>
            )}
          </nav>

          {related.length > 0 && (
            <section className="mt-12">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 mb-5">
                <i className="ri-movie-line text-primary-600"></i>
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {related.map((item) => (
                  <EpisodeCard key={item.id} episode={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}